import type { DirectoryUser, RoleNotice, RoomRoleStatus } from '@brush/api-client';

// `.ts` because this is a VALUE import and Node's ESM resolver does no extensionless lookup —
// the same constraint packages/ui's own pure modules carry. A type-only import is erased before
// Node sees it and stays bare.
import { ROLE_GRANT_PATH } from './portal.ts';

/**
 * Somebody a search offered, plus whether this room already has them.
 *
 * `alreadyLinked` is the screen's own knowledge — it comes from the room's operator list, not from
 * the directory — which is why it is added here rather than expected from the API.
 */
export type Candidate = DirectoryUser & { alreadyLinked?: boolean };

/** One staged person and the name THIS ROOM will know them by. */
export type Staged = { person: Candidate; name: string };

/**
 * Twenty, and it is the same twenty in three places: Graph caps a JSON batch at 20, the API caps
 * the staged list at 20 because link-time re-verification is one batch, and this stops an
 * administrator building a twenty-first only to have the whole thing refused on submit.
 *
 * <p>The screen is the convenience and the API is the control — never the other way round.</p>
 */
export const MAX_STAGED = 20;

/** The column is nvarchar(200). Entra's displayName runs to 256, so this is reachable. */
export const MAX_NAME = 200;

const has = (list: Staged[], objectId: string) =>
  list.some((s) => s.person.objectId === objectId);

/**
 * Why this person cannot be staged, or null.
 *
 * <p>
 * A REASON rather than a silent refusal. "Already staged", "already an operator here" and "the
 * list is full" are indistinguishable from the outside — the list simply does not grow — and only
 * a sentence tells the administrator which of the three happened to their click.
 * </p>
 */
export const stagingProblem = (list: Staged[], person: Candidate): string | null => {
  if (person.alreadyLinked) return `${person.displayName} already operates this room.`;
  if (has(list, person.objectId)) return `${person.displayName} is already in the list below.`;

  if (list.length >= MAX_STAGED) {
    return `You can add ${MAX_STAGED} people at a time. Link these first, then add more.`;
  }

  return null;
};

/**
 * Adds somebody, or returns the list unchanged when {@link stagingProblem} says no.
 *
 * <p>
 * It returns a NEW array and carries every existing entry through by reference, which is what
 * keeps an edited name from reverting: a version that re-derived names from the directory on each
 * stage would silently undo whatever had been typed, and would look like it had worked.
 * </p>
 */
export const stage = (list: Staged[], person: Candidate): Staged[] =>
  stagingProblem(list, person) === null
    ? [...list, { person, name: nameDraftFor(person).value }]
    : list;

export const unstage = (list: Staged[], objectId: string): Staged[] =>
  list.filter((s) => s.person.objectId !== objectId);

/**
 * The name to start from, and whether it needs attention before it can be linked.
 *
 * <p>
 * <b>NEVER TRUNCATED.</b> An over-length display name is kept whole and flagged, so the
 * administrator shortens it themselves. Cutting it here would store a name nobody chose, and the
 * person who would notice it was wrong is not the one at the keyboard.
 * </p>
 */
export const nameDraftFor = (person: Candidate) => ({
  value: person.displayName,
  flagged: person.displayName.trim().length > MAX_NAME,
});

/**
 * Why this name cannot be stored, or null.
 *
 * <p>
 * Judged on the TRIMMED name, because that is what the server stores. Judged untrimmed, a name
 * padded past the limit by trailing spaces would be refused here and accepted there — the client
 * stricter than the server, which reads as a bug in the product rather than as a rule.
 * </p>
 */
export const nameProblem = (name: string): string | null => {
  const trimmed = name.trim();

  if (trimmed.length === 0) return 'Give this operator a name for this room.';
  if (trimmed.length > MAX_NAME) return `A name can be at most ${MAX_NAME} characters.`;

  return null;
};

/**
 * Whether Link may be pressed, and why not.
 *
 * <p>
 * <b>The role status is never consulted.</b> It reports a grant this API cannot make, and linking
 * somebody before the role is granted is the ordinary order of events — refusing would have the
 * screen enforce a rule the server does not have, on information that is sometimes unknown.
 * </p>
 */
export const canLink = (list: Staged[]): { ok: boolean; reason: string } => {
  if (list.length === 0) return { ok: false, reason: 'Search for somebody to add.' };

  const unnamed = list.find((s) => nameProblem(s.name) !== null);

  return unnamed
    ? {
        ok: false,
        // Names the person, because a list of twenty with one empty box is a hunt otherwise.
        reason: `${unnamed.person.displayName}: ${nameProblem(unnamed.name)}`,
      }
    : { ok: true, reason: '' };
};

/** What the bulk endpoint takes. Trimmed here so the stored name is the one on screen. */
export const linkPayload = (list: Staged[]) =>
  list.map((s) => ({ objectId: s.person.objectId, name: s.name.trim() }));

export type RoleDisplay = {
  label: string;
  tone: 'ok' | 'warn' | 'muted';
  detail: string;
};

/**
 * How one role status reads.
 *
 * <p>
 * <b>Unknown is never rendered as not-granted, and the tones differ too.</b> "We asked and they do
 * not hold it" is actionable — go and grant it — while "we could not ask" is not, and showing the
 * first when the second is true sends an administrator to the Entra portal to fix something that
 * may not be wrong. An unrecognised value degrades to unknown for the same reason: a server that
 * grew a fourth state must not have every one of its rows read as a denial.
 * </p>
 */
export const roleDisplay = (status: RoomRoleStatus | string): RoleDisplay => {
  switch (status) {
    case 'granted':
      return {
        label: 'Room role granted',
        tone: 'ok',
        detail: 'They can sign in and manage the rooms they are linked to.',
      };

    case 'not_granted':
      return {
        label: 'No room role',
        tone: 'warn',
        detail:
          'They can still be linked, but cannot manage anything until a platform administrator ' +
          `grants them the Room operator role in Entra (${ROLE_GRANT_PATH}).`,
      };

    default:
      return {
        label: 'Room role not checked',
        tone: 'muted',
        detail: 'Linking is unaffected. See the note above for why it could not be checked.',
      };
  }
};

/**
 * Which list a notice is being written above. REQUIRED, not defaulted.
 *
 * <p>
 * The search results and the operator list carry separate role reads that fail independently, and
 * the same function writes the sentence above both. A default would silently give one of them the
 * other's wording, which is the exact defect this parameter was added to fix.
 * </p>
 */
export type NoticeScope = 'search' | 'operators';

/**
 * The sentence for a whole read's notice, or null when there is nothing to say.
 *
 * <p>
 * <b>Branched on the code, and none of the three may read as "no matches".</b> That is the failure
 * the notice exists to prevent: the results ARE on screen, and a sentence implying an empty
 * directory would send somebody hunting for an account that is right in front of them.
 * </p>
 * <p>
 * <b>It names WHAT it covers, never WHERE it sits.</b> The failed-role sentence used to end "so it
 * is not shown below" — and the operator list is also below it, carrying its own status from a
 * read that had succeeded. An operator reading "No room role" directly under a notice saying the
 * role was not shown is a contradiction, and it leaves neither sentence trustworthy. Two lists are
 * below this notice and only one of them failed, so position cannot identify it.
 * </p>
 * <p>
 * An unrecognised code prints the server's own message rather than guessing or falling silent, so
 * a code added server-side degrades to being shown — the same rule `UPSTREAM_CODES` follows.
 * </p>
 */
export const roleNoticeText = (
  notice: RoleNotice | null | undefined,
  scope: NoticeScope,
): string | null => {
  if (!notice) return null;

  switch (notice.code) {
    // The only code whose cause is per-read, and therefore the only one that is scoped. The other
    // two are facts about the tenant — the app role is missing for everybody, a group holds it for
    // everybody — so wording them per list would invent a distinction the server is not making.
    case 'role_read_failed':
      return scope === 'search'
        ? 'The directory could not be asked who holds the Room operator role for these search ' +
          'results, so it is not shown against them. Current operators are checked separately, ' +
          'and linking still works.'
        : 'The directory could not be asked who holds the Room operator role for the operators ' +
          'listed here, so it is not shown against them. Search results are checked separately.';

    case 'room_role_missing':
      return (
        'The Brush API app registration has no Room operator role defined, so nobody can hold ' +
        'it and nobody can be shown as holding it. A platform administrator needs to restore ' +
        'that app role in Entra.'
      );

    case 'role_held_by_group':
      return (
        'A group holds the Room operator role. Anyone without their own assignment may hold it ' +
        'through that group, and group membership is not read here — so their status is shown ' +
        'as not checked rather than guessed at.'
      );

    default:
      return notice.message;
  }
};

/**
 * Terms that find somebody in the local stand-in directory.
 *
 * DELIBERATELY ARTIFICIAL, AND NOT A CONVENIENCE. These are the fake's own invented people
 * (FakeDirectorySearch), so naming them here cannot disclose anybody and cannot go stale against a
 * tenant — a real colleague's first name would do both, and would read on a shared screen as this
 * product holding a list of staff.
 *
 * Three rather than one, because "maria" is the term that finds TWO people, which is the whole
 * point the panel makes about a name not being an identity. ONE COPY, read by both sentences
 * below, so neither can come to name somebody the fake does not hold while the other stays right.
 */
export const FAKE_DIRECTORY_EXAMPLES: readonly string[] = ['maria', 'tom', 'priya'];

/** "a, b or c" — the last join is `or`, since any one of them works. */
const anyOf = (items: readonly string[]) =>
  items.length < 2
    ? (items[0] ?? '')
    : `${items.slice(0, -1).join(', ')} or ${items[items.length - 1]}`;

/**
 * What to say above the results about the directory that answered.
 *
 * <p>NULL FOR A REAL DIRECTORY, and that is the load-bearing half. An administrator searching
 * their own tenant needs telling nothing — the results are their colleagues — and a notice calling
 * them invented would be worse than the silence this replaced. So the sentence exists only where
 * it is true, which is Development and Testing.</p>
 *
 * <p>It names people as well as naming the directory, because "this is not your tenant" leaves a
 * reader with nothing to type. Half an answer here is what sends somebody looking for a fault.</p>
 */
export const searchHint = (isFakeDirectory: boolean): string | null =>
  isFakeDirectory
    ? 'This is a local stand-in directory rather than your Entra tenant, and the people in it are '
      + `invented — no real colleague is here. Try ${anyOf(FAKE_DIRECTORY_EXAMPLES)}.`
    : null;

/**
 * What to say when the search found nobody.
 *
 * <p>TWO SENTENCES BECAUSE THE TWO DIRECTORIES SUPPORT DIFFERENT CLAIMS, and conflating them is
 * the defect this pair exists to fix. Against a real tenant, "nobody matches" is a fact about the
 * organisation and the guests-and-disabled clause is the useful part of it: both exclusions are
 * invisible, so an account that really is one reads as missing.</p>
 *
 * <p>Against the stand-in it is neither. The directory was never asked about the person, so it
 * cannot report them absent — and offering guests and disabled accounts as the likely reason sends
 * a reader off to check an account status that has nothing to do with it. That happened.</p>
 */
export const noMatchesText = (term: string, isFakeDirectory: boolean): string =>
  isFakeDirectory
    ? `The local stand-in directory holds nobody matching “${term}”, and it is not your tenant — `
      + 'so this says nothing about whether that person exists. '
      + `Try ${anyOf(FAKE_DIRECTORY_EXAMPLES)}.`
    : `Nobody in the directory matches “${term}”. Guests and disabled accounts are never shown.`;
